/**
 * Extracts the set of LN2 messages an adapter's CDO is configured to deliver
 * so the State tab can render one grey placeholder per CDO entry pre-traffic.
 * Each entry carries:
 *
 *   - `key`            unique React + dedup token; for non-position entries this
 *                      equals the `${bankId}:${liftId}:${typeLabel}:${valueLabel}`
 *                      that `applyDeltaToState` writes into the bitmask, so the
 *                      colour flips from grey → green/red on first reception.
 *   - `label`          display text (uses `::` separator + camelCase → spaced,
 *                      matching the FlatbufferMessagePicker style).
 *   - `positionTarget` set iff this is a Traffic.P (position) entry — resolved
 *                      against `positionMap` instead of the bitmask because
 *                      `applyElevatorTrafficDelta` writes positions there.
 *
 * Message sources, by protocol:
 *   - Inline JSON walkers (this file): Sequent, BACnet, OPC, Numato (GPIO +
 *     Relay), STM32 — each reads adapter.cdoInput / cdoInputJson.
 *   - FlatBuffer Key records (extractCdoEntriesFromKeys): LN1, RVU, CGA,
 *     LobbyVision, LN2 — mappings live in the /keys table, not in cdo_input.
 *   - Modbus: extractCdoEntriesFromModbusTemplate, fed by the unpacked
 *     /adapters/{id}/cdo binary (its cdoTemplateJson can't be parsed in JS).
 * MCE (external IDL file) and Diligent (empty CDO schema) carry no
 * frontend-enumerable message list and contribute nothing.
 */

import { Control } from "../../../flatbuffers/generated/control/control";
import { ControllerInfoContent } from "../../../flatbuffers/generated/controller-info/controller-info-content";
import { Fault } from "../../../flatbuffers/generated/fault/fault";
import { GroupControl } from "../../../flatbuffers/generated/group-control/group-control";
import { GroupSupervisoryStatus } from "../../../flatbuffers/generated/group-supervisory-status/group-supervisory-status";
import { Status } from "../../../flatbuffers/generated/status/status";
import { Traffic } from "../../../flatbuffers/generated/traffic/traffic";
import { Usage } from "../../../flatbuffers/generated/usage/usage";
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import { AdapterProtocol } from "../../../flatbuffers/generated/archive/adapter-protocol";
import {
  AdapterInterface,
  AdapterBacnetInterface,
  AdapterCdoSequentInterface,
  DiscreteToLn2EntryInterface,
} from "../../../interfaces/Adapter.interface";
import {
  AdapterCdoOpcInterface,
  OpcCmdToMessageInterface,
  OpcMappingsInterface,
} from "../../../interfaces/AdapterCdoOpc.interface";
import { KeyInterface } from "../../../interfaces/Key.interface";
import type { EditorModbusTemplate } from "../../setupWizard/steps/CdoModbusEditor";
import type { AdapterCdoOpcUa } from "../../../interfaces/AdapterCdoOpcUa.interface";
import {
  parseCompositeKey,
  categoryKeyToMessageContent,
} from "../../../flatbuffers/types/ln2MessageOptions";

interface LN2ValuedMessageJson {
  encoded_type: number;
  message_content: number;
  message_value: number;
  position?: number | null;
}

const TYPE_LABEL_BY_CONTENT: Record<number, string> = {
  [MessageContent.Status_StatusMessage]: "Status",
  [MessageContent.Traffic_TrafficMessage]: "Traffic",
  [MessageContent.Usage_UsageMessage]: "Usage",
  [MessageContent.Control_ControlMessage]: "Control",
  [MessageContent.Fault_FaultMessage]: "Fault",
  [MessageContent.GroupSupervisoryStatus_GroupSupervisoryStatusMessage]: "GroupSupervisoryStatus",
  [MessageContent.GroupControl_GroupControlMessage]: "GroupControl",
  [MessageContent.ControllerInfo_ControllerInfoMessage]: "ControllerInfo",
};

type FbsEnum = { [key: string]: number | string };

function buildReverseLookup(enumObj: FbsEnum): Record<number, string> {
  const lookup: Record<number, string> = {};
  Object.entries(enumObj).forEach(([name, value]) => {
    if (typeof value === "number") lookup[value] = name;
  });
  return lookup;
}

const ENUM_BY_CONTENT: Record<number, Record<number, string>> = {
  [MessageContent.Status_StatusMessage]: buildReverseLookup(Status),
  [MessageContent.Traffic_TrafficMessage]: buildReverseLookup(Traffic),
  [MessageContent.Usage_UsageMessage]: buildReverseLookup(Usage),
  [MessageContent.Control_ControlMessage]: buildReverseLookup(Control),
  [MessageContent.Fault_FaultMessage]: buildReverseLookup(Fault),
  [MessageContent.GroupSupervisoryStatus_GroupSupervisoryStatusMessage]:
    buildReverseLookup(GroupSupervisoryStatus),
  [MessageContent.GroupControl_GroupControlMessage]: buildReverseLookup(GroupControl),
  [MessageContent.ControllerInfo_ControllerInfoMessage]: buildReverseLookup(ControllerInfoContent),
};

/**
 * camelCase → spaced. `FrontDoorOpened` → `Front Door Opened`, `P1` stays `P1`
 * (no transform between letter and digit), single words are unchanged.
 */
export function humanizeCamel(s: string): string {
  return s.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
}

/** Result of resolving a CDO message descriptor to a renderable suffix pair. */
export interface SuffixPair {
  /** The single-colon suffix that matches `applyDeltaToState`'s bitmask key. */
  bitmaskSuffix: string;
  /** Double-colon display label with camelCase humanized. */
  displayLabel: string;
}

/**
 * Maps a LN2ValuedMessage descriptor to its bitmask suffix + display label.
 * Returns null for entries that don't belong in the bitmask (Service messages,
 * unresolvable enum values, blank/NONE entries).
 */
export function ln2MessageSuffix(msg: LN2ValuedMessageJson): SuffixPair | null {
  if (msg.message_content === MessageContent.Service_ServiceMessage) return null;
  if (msg.encoded_type === 0 && msg.message_content === 0 && msg.message_value === 0) return null;

  const typeLabel = TYPE_LABEL_BY_CONTENT[msg.message_content];
  if (!typeLabel) return null;

  const valueLabel = ENUM_BY_CONTENT[msg.message_content]?.[msg.message_value];
  if (typeof valueLabel !== "string" || valueLabel.length === 0) return null;

  return {
    bitmaskSuffix: `${typeLabel}:${valueLabel}`,
    displayLabel: `${humanizeCamel(typeLabel)}::${humanizeCamel(valueLabel)}`,
  };
}

/** Renderable CDO entry: one per row in the CDO Messages panel. */
export interface CdoExpectedEntry {
  /** Unique React key + dedup token. */
  key: string;
  /** Bank-prefixed key used to look up runtime bitmask state, or null for
   *  Traffic.P-position entries which resolve via positionMap instead. */
  bitmaskKey: string | null;
  /** Display text (uses `::` separator). */
  label: string;
  /** When set, this entry is a Traffic.P (position) message and should render
   *  green iff `positionMap[bankId:liftId] === positionTarget`. */
  positionTarget?: number;
}

// ---------------------------------------------------------------------------
// Per-protocol walkers — each yields { entryLift, message } pairs.
// ---------------------------------------------------------------------------

interface WalkedMessage {
  entryLift: string | undefined;
  message: LN2ValuedMessageJson;
}

function parseAdapterJson(raw: string | Uint8Array | null | undefined): unknown {
  if (raw === null || raw === undefined) return null;
  const asString = typeof raw === "string" ? raw : new TextDecoder().decode(raw);
  if (asString.length === 0) return null;
  try {
    return JSON.parse(asString);
  } catch {
    return null;
  }
}

function isSequentCdo(cdo: unknown): cdo is AdapterCdoSequentInterface {
  return !!cdo && typeof cdo === "object" && "discrete_2_message" in cdo && "dev_type" in cdo;
}

function walkSequent(adapter: AdapterInterface): WalkedMessage[] {
  const cdo =
    adapter.cdoInput && typeof adapter.cdoInput === "object"
      ? adapter.cdoInput
      : parseAdapterJson(adapter.cdoInputJson);
  if (!isSequentCdo(cdo)) return [];
  return cdo.discrete_2_message.map((entry: DiscreteToLn2EntryInterface) => ({
    entryLift: entry.value.lift_id?.id,
    message: entry.value.message,
  }));
}

function isBacnetConfig(cfg: unknown): cfg is AdapterBacnetInterface[] {
  return Array.isArray(cfg) && cfg.every((c) => c && typeof c === "object" && "entries" in c);
}

function walkBacnet(adapter: AdapterInterface): WalkedMessage[] {
  const parsed = adapter.bacnetConfig ?? parseAdapterJson(adapter.bacnetConfigJson);
  if (!isBacnetConfig(parsed)) return [];
  const out: WalkedMessage[] = [];
  parsed.forEach((cfg) => {
    cfg.entries?.forEach((entry) => {
      entry.descriptions?.forEach((desc) => {
        const ln2 = desc.ln2_msg;
        if (!ln2?.msg) return;
        out.push({
          entryLift: ln2.lift_id || undefined,
          message: ln2.msg,
        });
      });
    });
  });
  return out;
}

function isOpcCdo(cdo: unknown): cdo is AdapterCdoOpcInterface {
  return (
    !!cdo &&
    typeof cdo === "object" &&
    ("sites" in cdo || "groups" in cdo || "lifts" in cdo) &&
    "opc_server" in cdo
  );
}

function collectOpcMappings(mappings: OpcMappingsInterface | undefined, out: WalkedMessage[]) {
  if (!mappings?.opc_cmd_to_msg) return;
  mappings.opc_cmd_to_msg.forEach((entry: OpcCmdToMessageInterface) => {
    if (!entry?.value) return;
    out.push({ entryLift: undefined, message: entry.value });
  });
}

function walkOpc(adapter: AdapterInterface): WalkedMessage[] {
  const cdo =
    adapter.cdoInput && typeof adapter.cdoInput === "object"
      ? adapter.cdoInput
      : parseAdapterJson(adapter.cdoInputJson);
  if (!isOpcCdo(cdo)) return [];
  const out: WalkedMessage[] = [];
  collectOpcMappings(cdo.sites, out);
  collectOpcMappings(cdo.locations, out);
  collectOpcMappings(cdo.groups?.mappings, out);
  collectOpcMappings(cdo.lifts?.mappings, out);
  return out;
}

// ---------------------------------------------------------------------------
// Numato and STM32 inline-CDO walkers.
//
// Numato (GPIO + Relay) and STM32 persist each LN2 message inline in cdo_input
// as the same flat `{ message_content, message_value, ... }` quad the
// Sequent/OPC walkers emit — see ../../../flatbuffers/types/numatoFlatMessage.ts.
// The walkers below read that quad directly, so the shared `messageToExpected`
// path produces keys identical to the live bitmask regardless of which walker
// produced them.
// ---------------------------------------------------------------------------

/** True when the node is the flat `{encoded_type, message_content, message_value}`
 *  quad rather than the nested union. Guards against a pre-contract Numato or
 *  STM32 CDO still in the database: those carry no numeric message_content, so
 *  they yield no expected entries instead of a wrong one. */
function isFlatLn2Message(node: unknown): node is LN2ValuedMessageJson {
  if (!node || typeof node !== "object") return false;
  const m = node as Record<string, unknown>;
  return typeof m.message_content === "number" && typeof m.message_value === "number";
}

/** Numato (GPIO + Relay): LN2 messages live on `response_to_message[].value.msg`
 *  (device → LN2) and `message_to_cmd[].key` (LN2 → device command). Both are
 *  the flat quad LiftCloud's shared expander consumes — the same shape
 *  walkSequent reads — not the nested union the editors used to emit. */
function walkNumato(adapter: AdapterInterface): WalkedMessage[] {
  const cdo =
    adapter.cdoInput && typeof adapter.cdoInput === "object"
      ? adapter.cdoInput
      : parseAdapterJson(adapter.cdoInputJson);
  if (!cdo || typeof cdo !== "object") return [];
  const obj = cdo as { response_to_message?: unknown[]; message_to_cmd?: unknown[] };
  const out: WalkedMessage[] = [];
  const push = (message: unknown, lift: unknown) => {
    if (!isFlatLn2Message(message)) return;
    out.push({
      entryLift: typeof lift === "string" && lift.length > 0 ? lift : undefined,
      message,
    });
  };
  if (Array.isArray(obj.response_to_message)) {
    obj.response_to_message.forEach((entry) => {
      const value = (entry as { value?: { msg?: unknown; lift_id?: unknown } })?.value;
      push(value?.msg, value?.lift_id);
    });
  }
  if (Array.isArray(obj.message_to_cmd)) {
    // MessageToCommandMapping.key is a bare Ln2.ValuedMessage — no lift.
    obj.message_to_cmd.forEach((entry) => push((entry as { key?: unknown })?.key, undefined));
  }
  return out;
}

/** STM32: LN2 messages live on `msg_to_cmd[].key` (LN2 → device command), the
 *  flat quad — the same shape walkNumato reads off `message_to_cmd[].key`. */
function walkStm32(adapter: AdapterInterface): WalkedMessage[] {
  const cdo =
    adapter.cdoInput && typeof adapter.cdoInput === "object"
      ? adapter.cdoInput
      : parseAdapterJson(adapter.cdoInputJson);
  if (!cdo || typeof cdo !== "object") return [];
  const obj = cdo as { msg_to_cmd?: unknown[] };
  if (!Array.isArray(obj.msg_to_cmd)) return [];
  const out: WalkedMessage[] = [];
  obj.msg_to_cmd.forEach((entry) => {
    const message = (entry as { key?: unknown })?.key;
    if (isFlatLn2Message(message)) out.push({ entryLift: undefined, message });
  });
  return out;
}

const PROTOCOL_WALKERS: Partial<Record<AdapterProtocol, (a: AdapterInterface) => WalkedMessage[]>> =
  {
    [AdapterProtocol.SEQUENT]: walkSequent,
    [AdapterProtocol.BACNET]: walkBacnet,
    [AdapterProtocol.OPC]: walkOpc,
    [AdapterProtocol.NUMATOGPIO]: walkNumato,
    [AdapterProtocol.NUMATORELAY]: walkNumato,
    [AdapterProtocol.STM32]: walkStm32,
  };

function walkAdapter(adapter: AdapterInterface): WalkedMessage[] {
  const walker = PROTOCOL_WALKERS[adapter.protocolType as AdapterProtocol];
  return walker ? walker(adapter) : [];
}

// ---------------------------------------------------------------------------
// Entry construction
// ---------------------------------------------------------------------------

function messageToExpected(
  msg: LN2ValuedMessageJson,
  bankIdStr: string,
  liftIdStr: string,
): CdoExpectedEntry | null {
  const isTrafficPosition =
    msg.message_content === MessageContent.Traffic_TrafficMessage &&
    msg.message_value === Traffic.P &&
    typeof msg.position === "number";

  if (isTrafficPosition) {
    const pos = msg.position as number;
    return {
      key: `${bankIdStr}:${liftIdStr}:Traffic.P::${pos}`,
      bitmaskKey: null,
      label: `Traffic::P${pos}`,
      positionTarget: pos,
    };
  }

  const pair = ln2MessageSuffix(msg);
  if (!pair) return null;
  return {
    key: `${bankIdStr}:${liftIdStr}:${pair.bitmaskSuffix}`,
    bitmaskKey: `${bankIdStr}:${liftIdStr}:${pair.bitmaskSuffix}`,
    label: pair.displayLabel,
  };
}

/**
 * Reads `messageContent` / `messageValue` from a Key record returned by the
 * `/keys` endpoint, accepting both the FlatBuffer KeyT (camelCase) and the
 * JSON-fallback (snake_case) shapes.
 */
function readKeyMessageContent(key: KeyInterface): number | undefined {
  const camel = (key as { messageContent?: unknown }).messageContent;
  if (typeof camel === "number") return camel;
  const snake = (key as { message_content?: unknown }).message_content;
  return typeof snake === "number" ? snake : undefined;
}
function readKeyMessageValue(key: KeyInterface): number {
  const camel = (key as { messageValue?: unknown }).messageValue;
  if (typeof camel === "number") return camel;
  const snake = (key as { message_value?: unknown }).message_value;
  return typeof snake === "number" ? snake : 0;
}
function readKeyLowerContent(key: KeyInterface): number | undefined {
  const camel = (key as { lowerMessageContent?: unknown }).lowerMessageContent;
  if (typeof camel === "number") return camel;
  const snake = (key as { lower_message_content?: unknown }).lower_message_content;
  return typeof snake === "number" ? snake : undefined;
}
function readKeyLowerValue(key: KeyInterface): number {
  const camel = (key as { lowerMessageValue?: unknown }).lowerMessageValue;
  if (typeof camel === "number") return camel;
  const snake = (key as { lower_message_value?: unknown }).lower_message_value;
  return typeof snake === "number" ? snake : 0;
}

/**
 * Convert one Key record into a renderable CdoExpectedEntry.
 *
 * Mirrors `ln2MessageSuffix` but operates on FlatBuffer-decoded Key records
 * (Archive.Key, the LN1/RVU/CGA/LobbyVision adapter mappings) instead of the
 * legacy `cdo_input_json` payload.
 */
function keyMessageToExpected(
  messageContent: number,
  messageValue: number,
  bankIdStr: string,
  liftIdStr: string,
): CdoExpectedEntry | null {
  if (messageContent === MessageContent.Service_ServiceMessage) return null;
  const typeLabel = TYPE_LABEL_BY_CONTENT[messageContent];
  if (!typeLabel) return null;
  const valueLabel = ENUM_BY_CONTENT[messageContent]?.[messageValue];
  if (typeof valueLabel !== "string" || valueLabel.length === 0) return null;

  // Traffic.P arrives at positionMap[bank:lift], not bitmask. Without the
  // bank's floor list we cannot pre-expand per floor like the JSON walkers do,
  // so emit a single bitmask-keyed entry — applyDeltaToState writes
  // bank:lift:Traffic:P when any position frame arrives, lighting it green.
  const bitmaskSuffix = `${typeLabel}:${valueLabel}`;
  const compositeKey = `${bankIdStr}:${liftIdStr}:${bitmaskSuffix}`;
  return {
    key: compositeKey,
    bitmaskKey: compositeKey,
    label: `${humanizeCamel(typeLabel)}::${humanizeCamel(valueLabel)}`,
  };
}

function pushKeyEntries(
  key: KeyInterface,
  bankIdStr: string,
  liftIdStr: string,
  out: CdoExpectedEntry[],
  seen: Set<string>,
) {
  const mc = readKeyMessageContent(key);
  if (mc === undefined || mc === 0) return;
  const primary = keyMessageToExpected(mc, readKeyMessageValue(key), bankIdStr, liftIdStr);
  if (primary && !seen.has(primary.key)) {
    seen.add(primary.key);
    out.push(primary);
  }
  // Paired secondary key (`lower_*`) — a single Key record can carry two
  // mappings (e.g. high/low for a bit-paired state). Emit both.
  const lmc = readKeyLowerContent(key);
  if (lmc === undefined || lmc === 0) return;
  const secondary = keyMessageToExpected(lmc, readKeyLowerValue(key), bankIdStr, liftIdStr);
  if (secondary && !seen.has(secondary.key)) {
    seen.add(secondary.key);
    out.push(secondary);
  }
}

/**
 * Build expected entries from FlatBuffer Key records (Archive.Key from /keys).
 *
 * Used for LN1/RVU/CGA/LobbyVision and other protocols whose CDO mappings
 * live in the Keys table rather than embedded in `cdo_input_json`. All Keys
 * belong to the adapter (no lift_id on Key), so every key applies to the
 * given bank+lift scope.
 */
export function extractCdoEntriesFromKeys(
  keys: readonly KeyInterface[] | null | undefined,
  bankIdStr: string,
  liftIdStr: string,
): CdoExpectedEntry[] {
  if (!keys || keys.length === 0 || !bankIdStr || !liftIdStr) return [];
  const out: CdoExpectedEntry[] = [];
  const seen = new Set<string>();
  keys.forEach((key) => pushKeyEntries(key, bankIdStr, liftIdStr, out, seen));
  return out;
}

/**
 * Returns the list of expected entries for the given adapter, scoped to one
 * bank+lift. Pass an empty `liftIdStr` to get the union for the whole bank.
 *
 * Sources merged:
 *   1. FlatBuffer Key records (preferred — LN1/RVU/CGA/LobbyVision)
 *   2. Legacy JSON walkers (Sequent/BACnet/OPC embedded CDO)
 */
export function extractCdoExpectedEntries(
  adapter: AdapterInterface | null | undefined,
  bankIdStr: string,
  liftIdStr?: string,
  adapterKeys?: readonly KeyInterface[] | null,
): CdoExpectedEntry[] {
  if (!bankIdStr) return [];
  const out: CdoExpectedEntry[] = [];
  const seen = new Set<string>();
  const effectiveLift = liftIdStr || bankIdStr;

  // FlatBuffer-source path first so its entries win on key collisions.
  if (adapterKeys && adapterKeys.length > 0) {
    adapterKeys.forEach((k) => pushKeyEntries(k, bankIdStr, effectiveLift, out, seen));
  }

  if (!adapter) return out;
  const walked = walkAdapter(adapter);
  walked.forEach(({ entryLift, message }) => {
    if (liftIdStr && entryLift && entryLift !== liftIdStr) return;
    const entryLiftScope = entryLift ?? liftIdStr ?? bankIdStr;
    const expected = messageToExpected(message, bankIdStr, entryLiftScope);
    if (!expected || seen.has(expected.key)) return;
    seen.add(expected.key);
    out.push(expected);
  });
  return out;
}

/**
 * Walks the adapter's CDO once and groups expected entries by lift id. Bank-
 * level entries (no `lift_id`) appear under the `bankIdStr` key. Use this when
 * rendering multiple lift cards from one bank to avoid re-walking per lift.
 *
 * `adapterKeys` (FlatBuffer Key records) are applied to every lift the caller
 * later queries — Keys have no per-lift scope, so they belong to every bucket
 * the bank renders. The caller seeds the bucket via `seedLiftIds`.
 */
export function extractCdoEntriesByLift(
  adapter: AdapterInterface | null | undefined,
  bankIdStr: string,
  adapterKeys?: readonly KeyInterface[] | null,
  seedLiftIds?: readonly string[],
): Map<string, CdoExpectedEntry[]> {
  const byLift = new Map<string, CdoExpectedEntry[]>();
  if (!bankIdStr) return byLift;
  const seenByLift = new Map<string, Set<string>>();

  const ensureBucket = (lift: string): { bucket: CdoExpectedEntry[]; seen: Set<string> } => {
    let seen = seenByLift.get(lift);
    if (!seen) {
      seen = new Set<string>();
      seenByLift.set(lift, seen);
    }
    let bucket = byLift.get(lift);
    if (!bucket) {
      bucket = [];
      byLift.set(lift, bucket);
    }
    return { bucket, seen };
  };

  if (adapterKeys && adapterKeys.length > 0) {
    const liftIds = seedLiftIds && seedLiftIds.length > 0 ? seedLiftIds : [bankIdStr];
    liftIds.forEach((lift) => {
      const { bucket, seen } = ensureBucket(lift);
      adapterKeys.forEach((k) => pushKeyEntries(k, bankIdStr, lift, bucket, seen));
    });
  }

  if (!adapter) return byLift;
  const walked = walkAdapter(adapter);
  walked.forEach(({ entryLift, message }) => {
    const lift = entryLift ?? bankIdStr;
    const expected = messageToExpected(message, bankIdStr, lift);
    if (!expected) return;
    const { bucket, seen } = ensureBucket(lift);
    if (seen.has(expected.key)) return;
    seen.add(expected.key);
    bucket.push(expected);
  });
  return byLift;
}

// ---------------------------------------------------------------------------
// Modbus — enumerated from the unpacked ModbusTemplate.
//
// The Modbus CDO cannot be parsed from `cdoTemplateJson` (Go's Type/Value union
// wrappers break the TS FlatBuffer Object API — see AdapterEditor's load path),
// so the caller fetches the `/adapters/{id}/cdo` FlatBuffer binary and unpacks
// it (see `useModbusCdoTemplate`). Each read/write entry carries its LN2 message
// as a composite-key string ("<category>:<variant>:<value>"), which we convert
// to the same flat shape the JSON walkers use.
// ---------------------------------------------------------------------------

/** Convert a Modbus composite-key string ("category:variant:value") to the flat
 *  `LN2ValuedMessageJson` shape. */
function compositeKeyToLn2Json(compositeKey: string): LN2ValuedMessageJson | null {
  const parsed = parseCompositeKey(compositeKey);
  if (!parsed) return null;
  const messageContent = categoryKeyToMessageContent(parsed.categoryKey);
  if (messageContent === MessageContent.NONE) return null;
  return { encoded_type: 0, message_content: messageContent, message_value: parsed.enumValue };
}

/** Parse every read/write entry's LN2 message once (reads contribute their
 *  primary + paired message; writes their single message). Lift-agnostic — the
 *  bank/lift scope is applied later by `scopeModbusMessages`. */
function modbusTemplateMessages(
  template: Pick<EditorModbusTemplate, "reads" | "writes">,
): LN2ValuedMessageJson[] {
  const messages: LN2ValuedMessageJson[] = [];
  const add = (msgId: string | null | undefined) => {
    if (!msgId) return;
    const message = compositeKeyToLn2Json(msgId);
    if (message) messages.push(message);
  };
  (template.reads ?? []).forEach((read) => {
    add(read.msgId);
    add(read.pairedMsgId);
  });
  (template.writes ?? []).forEach((write) => add(write.msgId));
  return messages;
}

/** Scope a parsed message list to one `bankIdStr:liftIdStr` pair, deduped by
 *  key, so each Modbus tag flips grey → green/red exactly like every other
 *  protocol's CDO message. */
function scopeModbusMessages(
  messages: readonly LN2ValuedMessageJson[],
  bankIdStr: string,
  liftIdStr: string,
): CdoExpectedEntry[] {
  const out: CdoExpectedEntry[] = [];
  const seen = new Set<string>();
  messages.forEach((message) => {
    const expected = messageToExpected(message, bankIdStr, liftIdStr);
    if (!expected || seen.has(expected.key)) return;
    seen.add(expected.key);
    out.push(expected);
  });
  return out;
}

/**
 * Build expected entries from an unpacked Modbus CDO template, scoped to one
 * bank+lift. Pass an empty `liftIdStr` to scope to the bank.
 */
export function extractCdoEntriesFromModbusTemplate(
  template: Pick<EditorModbusTemplate, "reads" | "writes"> | null | undefined,
  bankIdStr: string,
  liftIdStr?: string,
): CdoExpectedEntry[] {
  if (!template || !bankIdStr) return [];
  return scopeModbusMessages(modbusTemplateMessages(template), bankIdStr, liftIdStr || bankIdStr);
}

/**
 * Like `extractCdoEntriesFromModbusTemplate` but groups by lift, parsing the
 * template only once and re-scoping per lift. Use this when rendering many lift
 * cards from one bank so the (potentially large) template isn't re-walked on
 * every real-time monitoring re-render.
 */
export function extractCdoEntriesFromModbusTemplateByLift(
  template: Pick<EditorModbusTemplate, "reads" | "writes"> | null | undefined,
  bankIdStr: string,
  liftIds: readonly string[],
): Map<string, CdoExpectedEntry[]> {
  const byLift = new Map<string, CdoExpectedEntry[]>();
  if (!template || !bankIdStr || liftIds.length === 0) return byLift;
  const messages = modbusTemplateMessages(template);
  liftIds.forEach((lift) => {
    byLift.set(lift, scopeModbusMessages(messages, bankIdStr, lift || bankIdStr));
  });
  return byLift;
}

// ---------------------------------------------------------------------------
// OPC UA — enumerated from the decoded OpcUa.MappingT.
//
// Like Modbus, the OPC UA CDO is union-heavy (Identity, NodeIdentifier,
// Data/Variant, MonitorMode, FilterOperand) so it cannot be parsed from
// `cdoTemplateJson`, so the caller fetches the `/adapters/{id}/cdo`
// FlatBuffer binary and decodes it (see `useOpcUaCdoMapping`). Every LN2
// message reference (node<->message mappings, event-type mappings, method
// mappings) carries its message as a composite-key string
// ("<category>:<variant>:<value>"), which we convert to the same flat shape
// the JSON walkers use, bank-scope v1 (per-lift breakdown is not modeled by
// nodeToMsg/msgToNode/eventMappings/methods — every entry applies to the
// whole bank until a per-lift node scoping convention is added).
// ---------------------------------------------------------------------------

/** Parse every node<->message, event-type, and method mapping's LN2 message once. Lift-agnostic — bank/lift scope applied later. Reuses the Modbus composite-key parser (same "category:variant:value" convention). */
function opcUaMappingMessages(
  mapping: Pick<AdapterCdoOpcUa, "nodeToMsg" | "msgToNode" | "eventMappings" | "methods">,
): LN2ValuedMessageJson[] {
  const messages: LN2ValuedMessageJson[] = [];
  const add = (msgId: string | null | undefined) => {
    if (!msgId) return;
    const message = compositeKeyToLn2Json(msgId);
    if (message) messages.push(message);
  };
  (mapping.nodeToMsg ?? []).forEach((row) => add(row.msgId));
  (mapping.msgToNode ?? []).forEach((row) => add(row.msgId));
  (mapping.eventMappings ?? []).forEach((row) => add(row.msgId));
  (mapping.methods ?? []).forEach((row) => add(row.msgId));
  return messages;
}

/** Scope a parsed OPC UA message list to one `bankIdStr:liftIdStr` pair, deduped by key. */
function scopeOpcUaMessages(
  messages: readonly LN2ValuedMessageJson[],
  bankIdStr: string,
  liftIdStr: string,
): CdoExpectedEntry[] {
  const out: CdoExpectedEntry[] = [];
  const seen = new Set<string>();
  messages.forEach((message) => {
    const expected = messageToExpected(message, bankIdStr, liftIdStr);
    if (!expected || seen.has(expected.key)) return;
    seen.add(expected.key);
    out.push(expected);
  });
  return out;
}

/**
 * Build expected entries from a decoded OPC UA CDO mapping, scoped to one
 * bank+lift. Pass an empty `liftIdStr` to scope to the bank.
 */
export function extractCdoEntriesFromOpcUaMapping(
  mapping:
    | Pick<AdapterCdoOpcUa, "nodeToMsg" | "msgToNode" | "eventMappings" | "methods">
    | null
    | undefined,
  bankIdStr: string,
  liftIdStr?: string,
): CdoExpectedEntry[] {
  if (!mapping || !bankIdStr) return [];
  return scopeOpcUaMessages(opcUaMappingMessages(mapping), bankIdStr, liftIdStr || bankIdStr);
}

/**
 * Like `extractCdoEntriesFromOpcUaMapping` but groups by lift, parsing the
 * mapping only once and re-scoping per lift. Use this when rendering many
 * lift cards from one bank so the mapping isn't re-walked on every
 * real-time monitoring re-render.
 */
export function extractCdoEntriesFromOpcUaMappingByLift(
  mapping:
    | Pick<AdapterCdoOpcUa, "nodeToMsg" | "msgToNode" | "eventMappings" | "methods">
    | null
    | undefined,
  bankIdStr: string,
  liftIds: readonly string[],
): Map<string, CdoExpectedEntry[]> {
  const byLift = new Map<string, CdoExpectedEntry[]>();
  if (!mapping || !bankIdStr || liftIds.length === 0) return byLift;
  const messages = opcUaMappingMessages(mapping);
  liftIds.forEach((lift) => {
    byLift.set(lift, scopeOpcUaMessages(messages, bankIdStr, lift || bankIdStr));
  });
  return byLift;
}
